// src/utils/nodeUtils.js

// src/utils/nodeUtils.js

export function formatTemplate(template, fields = {}) {
  if (!template) return "";

  try {
    // ✅ If template is a function (new style)
    if (typeof template === "function") {
      return template(fields) || "";
    }

    // ✅ Fallback: old string template with {placeholders}
    if (typeof template === "string") {
      return template.replace(/\{([^}]+)\}/g, (match, p1) => {
        const val = fields[p1];
        if (val === undefined || val === null) return "";
        if (typeof val === "object") return JSON.stringify(val);
        return String(val);
      });
    }

    return "";
  } catch (e) {
    console.error("Error formatting template:", e);
    return "";
  }
}

/**
 * Walk the tree and build a flat list of nodes with nested numbering.
 * nodeConfigs is required to render preview strings.
 *
 * Returns array of { id, numbering, depth, text, node }
 */
export function buildNumberedList(root, nodeConfigs = {}) {
  const lines = [];

  function walk(node, prefix = [], depth = 0) {
    const numbering = prefix.join(".");
    const cfg = nodeConfigs?.[node.node] ?? {};
    let text;
    if (node.msg) {
      text = `${node.node} - ${node.msg}`;
    } else if (cfg.template) {
      try {
        text = formatTemplate(cfg.template, node.fields || {});
      } catch (e) {
        text = node.node;
      }
    } else {
      text = node.node;
    }

    const display = numbering ? `${text}` : text;
    lines.push({ id: node.id, numbering, depth, text: display, node });

    // child nodes
    if (node.nodes && node.nodes.length > 0) {
      for (let i = 0; i < node.nodes.length; i++) {
        walk(node.nodes[i], prefix.concat(i + 1), depth + 1);
      }
    }

    // outcome branches
    if (node.outcomes) {
      Object.entries(node.outcomes).forEach(([outcomeKey, arr]) => {
        const outcomeHeader = {
          id: `${node.id}::outcome::${outcomeKey}`,
          numbering: "",
          depth: depth,
          text: `Outcome of Rule ${prefix.join(".") || "root"} - ${outcomeKey}`,
          node: null,
          isOutcomeHeader: true,
        };
        lines.push(outcomeHeader);

        for (let j = 0; j < arr.length; j++) {
          walk(arr[j], prefix.concat(j + 1), depth + 1);
        }
      });
    }
  }

  if (!root) return lines;
  if (root.id && root.id !== "root") {
    walk(root, [1], 0);
  } else if (root.nodes && root.nodes.length > 0) {
    for (let i = 0; i < root.nodes.length; i++) {
      walk(root.nodes[i], [i + 1], 0);
    }
  } else {
    walk(root, [1], 0);
  }

  return lines;
}

export function findNodeById(root, id) {
  if (!root) return null;
  if (root.id === id) return root;
  if (root.nodes) {
    for (const child of root.nodes) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }
  if (root.outcomes) {
    for (const arr of Object.values(root.outcomes)) {
      for (const child of arr) {
        const found = findNodeById(child, id);
        if (found) return found;
      }
    }
  }
  return null;
}
